import { createContext, useCallback, useMemo, useState } from "react";
import { PatientSubscriptions } from "src/api/types";
import ChangeSubscriptionModal from "./components/ChangeSubscriptionModal";
import CancelSubscriptionModal from "./components/CancelSubscriptionModal";
import CreateSubscriptionModal from "./components/CreateSubscriptionModal";
import AddCouponModal from "./components/AddCouponModal";
import ScheduleReactivationModal from "./components/ScheduleReactivationModal";

type ContextType = {
  subscription: PatientSubscriptions | null;
  handleOpenChangePlan: (sub: PatientSubscriptions | null) => void;
  handleOpenCancelPlan: (sub: PatientSubscriptions | null) => void;
  handleOpenCreatePlan: () => void;
  handleOpenAddCoupon: (sub: PatientSubscriptions | null) => void;
  handleOpenScheduleReactivation: (sub: PatientSubscriptions | null) => void;
};

export const SubscriptionModalContext = createContext<ContextType>({
  subscription: null,
  handleOpenChangePlan: () => {},
  handleOpenCancelPlan: () => {},
  handleOpenCreatePlan: () => {},
  handleOpenAddCoupon: () => {},
  handleOpenScheduleReactivation: () => {},
});

interface Props {
  children: React.ReactNode;
  patientId: number;
  patientGender: string | null;
}

const SubscriptionModalProvider = ({
  children,
  patientId,
  patientGender,
}: Props) => {
  const [subscription, setSubscription] = useState<PatientSubscriptions | null>(
    null,
  );
  const [openChangePlan, setOpenChangePlan] = useState<boolean>(false);
  const [openCreatePlan, setOpenCreatePlan] = useState<boolean>(false);
  const [openCancelPlan, setOpenCancelPlan] = useState<boolean>(false);
  const [openAddCoupon, setOpenAddCoupon] = useState<boolean>(false);
  const [openScheduleReactivation, setOpenScheduleReactivation] =
    useState<boolean>(false);

  const handleOpenScheduleReactivation = useCallback(
    (subscription: PatientSubscriptions | null) => {
      setSubscription(subscription);
      setOpenScheduleReactivation(true);
    },
    [],
  );

  const handleCloseScheduleReactivation = useCallback(() => {
    setSubscription(null);
    setOpenScheduleReactivation(false);
  }, []);

  const handleCloseChangePlan = useCallback(() => {
    setSubscription(null);
    setOpenChangePlan(false);
  }, []);

  const handleOpenChangePlan = useCallback(
    (subscription: PatientSubscriptions | null) => {
      setSubscription(subscription);
      setOpenChangePlan(true);
    },
    [],
  );

  const handleCloseCancelPlan = useCallback(() => {
    setSubscription(null);
    setOpenCancelPlan(false);
  }, []);

  const handleOpenCancelPlan = useCallback(
    (subscription: PatientSubscriptions | null) => {
      setSubscription(subscription);
      setOpenCancelPlan(true);
    },
    [],
  );

  const handleCloseCreatePlan = useCallback(() => {
    setOpenCreatePlan(false);
  }, []);

  const handleOpenCreatePlan = useCallback(() => {
    setOpenCreatePlan(true);
  }, []);

  const handleOpenAddCoupon = useCallback(
    (subscription: PatientSubscriptions | null) => {
      setSubscription(subscription);
      setOpenAddCoupon(true);
    },
    [],
  );

  const handleCloseAddCoupon = useCallback(() => {
    setOpenAddCoupon(false);
  }, []);

  const value = useMemo(
    () => ({
      subscription,
      handleOpenChangePlan,
      handleOpenCancelPlan,
      handleOpenCreatePlan,
      handleOpenAddCoupon,
      handleOpenScheduleReactivation,
    }),
    [
      subscription,
      handleOpenChangePlan,
      handleOpenCancelPlan,
      handleOpenCreatePlan,
      handleOpenAddCoupon,
      handleOpenScheduleReactivation,
    ],
  );

  return (
    <SubscriptionModalContext.Provider value={value}>
      {children}
      {subscription ? (
        <>
          <ChangeSubscriptionModal
            open={openChangePlan}
            subscription={subscription}
            onClose={handleCloseChangePlan}
          />
          <CancelSubscriptionModal
            subscription={subscription}
            onClose={handleCloseCancelPlan}
            open={openCancelPlan}
          />
          <AddCouponModal
            patientId={patientId}
            open={openAddCoupon}
            onClose={handleCloseAddCoupon}
            subscription={subscription}
          />
          <ScheduleReactivationModal
            open={openScheduleReactivation}
            onClose={handleCloseScheduleReactivation}
            subscription={subscription}
          />
        </>
      ) : null}
      <CreateSubscriptionModal
        patientId={patientId}
        onClose={handleCloseCreatePlan}
        open={openCreatePlan}
        patientGender={patientGender}
      />
    </SubscriptionModalContext.Provider>
  );
};

export default SubscriptionModalProvider;
